import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface PreQuizVideoProps {
  videoUrl: string;
  isChildTheme?: boolean;
  onContinue: () => void;
}

type Step = "prompt" | "player";

/** Convert a YouTube watch URL to an embed URL with no related videos / no download UI. */
function toYouTubeEmbed(url: string): string | null {
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (!yt) return null;
  return `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1&disablekb=0&fs=0&playsinline=1`;
}

/** Convert a Vimeo URL to an embed URL. */
function toVimeoEmbed(url: string): string | null {
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (!vm) return null;
  return `https://player.vimeo.com/video/${vm[1]}?dnt=1&byline=0&portrait=0&title=0`;
}

function resolveVideo(url: string): { kind: "iframe"; src: string } | { kind: "html5"; src: string } {
  const yt = toYouTubeEmbed(url);
  if (yt) return { kind: "iframe", src: yt };
  const vm = toVimeoEmbed(url);
  if (vm) return { kind: "iframe", src: vm };
  return { kind: "html5", src: url };
}

export function PreQuizVideo({ videoUrl, isChildTheme, onContinue }: PreQuizVideoProps) {
  const [step, setStep] = useState<Step>("prompt");
  const videoRef = useRef<HTMLVideoElement>(null);
  const resolved = resolveVideo(videoUrl);

  return (
    <AnimatePresence mode="wait">
      {step === "prompt" ? (
        <motion.div
          key="prompt"
          className="flex flex-1 flex-col items-center justify-center p-6 text-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.28 }}
        >
          <div className="mb-5 text-5xl select-none" aria-hidden>🎬</div>
          <h2
            className="text-xl font-bold text-slate-800 mb-2"
            style={{ fontFamily: "var(--lp-font-heading)" }}
          >
            {isChildTheme ? "Extra video! 🍿" : "Extra material"}
          </h2>
          <p className="text-sm text-slate-500 max-w-xs mb-8">
            {isChildTheme
              ? "There's a bonus video for you! Want to watch it before the quiz?"
              : "There's an extra video related to this lesson. Watch it before the quiz, or skip straight to the questions."}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
            <motion.button
              type="button"
              onClick={() => setStep("player")}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-white shadow-lg"
              style={{ background: "linear-gradient(90deg, var(--lp-header-from), var(--lp-header-to))" }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <PlayIcon />
              {isChildTheme ? "Yes, watch it!" : "Watch video"}
            </motion.button>
            <motion.button
              type="button"
              onClick={onContinue}
              className="flex-1 px-5 py-3 rounded-full font-semibold text-slate-600 bg-white border-2 border-slate-200 shadow hover:bg-slate-50"
              whileTap={{ scale: 0.97 }}
            >
              Skip to quiz →
            </motion.button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="player"
          className="flex flex-1 flex-col min-h-0 p-4 gap-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <h2
            className="text-base font-semibold text-slate-700 shrink-0"
            style={{ fontFamily: "var(--lp-font-heading)" }}
          >
            {isChildTheme ? "🎬 Watch the video" : "Lesson introduction video"}
          </h2>

          {/* Video container — aspect ratio 16/9 */}
          <div className="relative w-full rounded-xl overflow-hidden bg-black shadow-lg shrink-0"
            style={{ aspectRatio: "16/9" }}>
            {resolved.kind === "iframe" ? (
              <iframe
                src={resolved.src}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                // Deliberately omit allowFullScreen to discourage downloading
                sandbox="allow-scripts allow-same-origin allow-presentation"
                title="Lesson video"
              />
            ) : (
              <video
                ref={videoRef}
                src={resolved.src}
                className="absolute inset-0 w-full h-full"
                controls
                autoPlay
                playsInline
                // Prevents the download button in the native controls
                {...({ controlsList: "nodownload" } as object)}
                disablePictureInPicture
                // Block right-click save
                onContextMenu={(e) => e.preventDefault()}
              />
            )}
          </div>

          <div className="shrink-0 flex gap-3 justify-center flex-wrap">
            <motion.button
              type="button"
              onClick={onContinue}
              className="px-5 py-2.5 rounded-full font-semibold text-slate-600 bg-white border-2 border-slate-200 shadow hover:bg-slate-50 text-sm"
              whileTap={{ scale: 0.97 }}
            >
              Skip to quiz →
            </motion.button>
            <motion.button
              type="button"
              onClick={onContinue}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-white shadow-lg text-sm"
              style={{ background: "linear-gradient(90deg, var(--lp-header-from), var(--lp-header-to))" }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Done — go to quiz →
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PlayIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7L8 5z" />
    </svg>
  );
}
